// src/components/PlanBadge.jsx
//
// The plan / Guardian Dubai indicator, shared by the navigator and patient
// portals so a patient reads the same way wherever they appear.
//
// Deepu, 12 Aug 2026: "Indicator for Patient and Navigator portal that a
// patient is a Dubai customer. If not mapped to a subscriber that is linked to
// Dubai program, then check the subscription. If it's null, then show Record
// Vault Plan. If not null, show the appropriate one — Navigator Plan, Family
// Plan."
//
// That rule is NOT implemented here. The backend resolves it once in
// PatientPlanCatalog.java and ships the answer on PatientDTO.Response as
// planLabel / planCode / guardianDubai; this component only picks a colour and
// renders it. Deriving it a second time in the browser is how the navigator
// portal and the patient portal would end up disagreeing about who is a Dubai
// customer — the same drift the doctor portal's risk badge had before it was
// consolidated.
//
// If a caller passes a patient object that predates those fields (a stale
// cached record, say), nothing renders. No badge is always better than a
// confidently wrong one on a clinical screen.

const BADGE_CLASS = {
  GUARDIAN_DUBAI: 'badge-purple',    // a program, not a domestic tier
  FAMILY: 'badge-confirmed',
  NAVIGATOR: 'badge-confirmed',
  RECORD_VAULT: 'badge-unknown',     // the baseline everyone is on
};

/**
 * @param {object}  props.patient   A PatientDTO.Response (or the patient
 *                                  portal's own profile — same shape).
 * @param {boolean} props.showDubaiOnly  Render only when the patient is a
 *                                  Dubai customer, staying out of the way on
 *                                  dense screens that already show a plan.
 * @param {string}  props.className Extra classes, e.g. a size modifier.
 */
export default function PlanBadge({ patient, showDubaiOnly = false, className = '' }) {
  if (!patient?.planLabel) return null;

  const isDubai = Boolean(patient.guardianDubai);
  if (showDubaiOnly && !isDubai) return null;

  const key = isDubai ? 'GUARDIAN_DUBAI' : patient.planCode;
  const tone = BADGE_CLASS[key] || 'badge-unknown';

  return (
    <span
      className={`badge ${tone} ${className}`.trim()}
      title={isDubai
        ? 'Enrolled under the Guardian Dubai program by a subscriber'
        : `Subscription: ${patient.planLabel}`}
    >
      {isDubai ? '🇦🇪 ' : ''}{patient.planLabel}
    </span>
  );
}
